import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { projectService } from '../services/api';
import { ChevronRight, Upload, Eye, Play, BarChart2, Trash2, Loader2 } from 'lucide-react';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "../components/ui/table";
import CSVViewer from './CSVViewer';

const ProjectDetail = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();  // Add this line
  const [project, setProject] = useState(null);
  const [upload_files, setuploadFiles] = useState([]);
  const [working_files, setworkingFiles] = useState([]);
  const [reports_files, setreportsFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [uploadStatus, setUploadStatus] = useState('idle');
  const [processingFiles, setProcessingFiles] = useState({});
  const [processMessage, setProcessMessage] = useState('');
  const { user } = useAuth(); // Change from token to user

  useEffect(() => {
    fetchProjectDetails();
  }, [projectId, user]); // Add user to dependencies

// Add state for viewed file content
const [viewedFile, setViewedFile] = useState({
    content: null,
    name: null
  });

// Update the fetchProjectDetails function in ProjectDetail.js
const fetchProjectDetails = async () => {
    try {
      setLoading(true);
      setProcessingFiles({}); // Reset processing states
      setProcessMessage(''); // Clear any process messages
      const token = user?.token || localStorage.getItem('token');
      
      if (!token) {
        console.error('No token available');
        navigate('/login');
        return;
      }

      console.log('Fetching project details. Token present:', !!token);
      
      const projectData = await projectService.getProjectDetails(projectId, token);
      console.log('Received project data:', projectData);
      
      setProject(projectData);
      
      // Check if files arrays exist and log them
      const uploadFilesData = projectData.upload_files || [];
      const workingFilesData = projectData.working_files || [];
      const reportsFilesData = projectData.reports_files || [];
      
      console.log('upload Files data:', uploadFilesData);
      console.log('working Files data:', workingFilesData);
      console.log('reports Files data:', reportsFilesData);

      setuploadFiles(uploadFilesData);
      setworkingFiles(workingFilesData);
      setreportsFiles(reportsFilesData);
      
      setError('');
    } catch (err) {
      console.error('Error fetching project details:', err);
      setError(err.message || 'Error loading project details');
      if (err.message.includes('Invalid token')) {
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (file) => {
    try {
      const token = user?.token || localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token available');
      }
  
      setError('');
      setUploadStatus('uploading');
  
      console.log('Starting file upload process for:', file.name);
  
      // Get the pre-signed URL
      const response = await fetch(`${process.env.REACT_APP_API_URL}/projects/${projectId}/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          fileName: file.name,
          fileType: file.type
        })
      });
  
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to get upload URL');
      }
  
      const { uploadUrl } = await response.json();
      console.log('Received presigned URL');
  
      // Upload the file to S3
      const uploadResponse = await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type
        }
      });
  
      if (!uploadResponse.ok) {
        throw new Error('Failed to upload file to S3');
      }
  
      console.log('File uploaded successfully');
      setUploadStatus('success');
      setTimeout(() => setUploadStatus('idle'), 3000);
  
      // Refresh the file list
      await fetchProjectDetails();
    } catch (err) {
      console.error('Error uploading file:', err);
      setError(err.message || 'Error uploading file');
      setUploadStatus('error');
    }
  };

  const handleProcessFile = async (fileId, fileName) => {
    try {
      const token = user?.token || localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token available');
      }
  
      setError('');
      // Set processing state for this specific file
      setProcessingFiles(prev => ({ ...prev, [fileId]: true }));
      setProcessMessage(`Processing ${fileName}...`);
  
      await projectService.processProjectFile(projectId, fileId, token);
      
      setProcessMessage(`Successfully processed ${fileName}`);
      // Clear success message after 3 seconds
      setTimeout(() => setProcessMessage(''), 3000);
      
      // Refresh the file list
      await fetchProjectDetails();
    } catch (err) {
      console.error('Error processing file:', err);
      setError(err.message || `Error processing ${fileName}`);
    } finally {
      // Clear processing state
      setProcessingFiles(prev => ({ ...prev, [fileId]: false }));
    }
  };

  const handleDeleteFile = async (fileId, fileName) => {
    if (!window.confirm(`Are you sure you want to delete ${fileName}?`)) return;
  
    try {
      const token = user?.token || localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token available');
      }
  
      setError('');
      await projectService.deleteProjectFile(projectId, fileId, token);
      // Clear viewed file if it's the one being deleted
      console.info('viewedFile.name: ' + viewedFile.name + ' fileName: '  + fileName )
      if (viewedFile.name === fileName) {
        setViewedFile({ content: null, name: null });
      }
      await fetchProjectDetails();
    } catch (err) {
      console.error('Error deleting file:', err);
      setError(err.message || 'Error deleting file');
    }
  };

   const handleViewFile = async (fileId, fileName) => {
    try {
      const token = user?.token || localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token available');
      }

      setViewedFile({ content: null, name: fileName }); // Reset content but set name
      const response = await projectService.getProjectFileContent(projectId, fileId, token);
      setViewedFile({
        content: response.content,
        name: fileName
      });
      setError('');
    } catch (err) {
      console.error('Error viewing file:', err);
      setError(err.message || 'Error viewing file');
      setViewedFile({ content: null, name: null });
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (!project) {
    return <div className="text-red-500">Project not found</div>;
  }

  // Rest of your JSX remains the same
  return (
    <div className="container mx-auto px-4 py-8">
      {/* Existing breadcrumb and project title */}
      <div className="flex items-center mb-6 text-sm">
        <Link to="/projects" className="text-blue-600 hover:text-blue-800">
          Projects
        </Link>
        <ChevronRight className="h-4 w-4 mx-2" />
        <span className="font-medium">{project.project_name}</span>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {/* Link to SprintAnalytics */}
    <div className="flex justify-between items-center mb-8">
    <Link 
        to={`/projects/${projectId}/sprintanalytics`} 
        className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
    >
        <BarChart2 className="h-5 w-5 mr-2" />
        Sprint Analytics
    </Link>
    </div>

      {/* File Upload section */}
      <div className="mb-8">
        <input
          type="file"
          accept=".csv"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) handleFileUpload(file);
          }}
          className="hidden"
          id="file-upload"
        />
        <label
          htmlFor="file-upload"
          className={`inline-flex items-center justify-center gap-2 px-4 py-2 rounded cursor-pointer ${
            uploadStatus === 'uploading'
              ? 'bg-yellow-500 hover:bg-yellow-600'
              : uploadStatus === 'success'
              ? 'bg-green-500 hover:bg-green-600'
              : uploadStatus === 'error'
              ? 'bg-red-500 hover:bg-red-600'
              : 'bg-blue-500 hover:bg-blue-600'
          } text-white transition-colors`}
        >
          {uploadStatus === 'uploading' ? (
            <>
              <Loader2 className="h-5 w-5 animate-spin" />
              Uploading...
            </>
          ) : (
            <>
              <Upload className="h-5 w-5" />
              Upload CSV
            </>
          )}
        </label>
      </div>

      {/* Uploaded Files Table */}
      <h3 className="text-3xl font-bold"> Uploaded Sprint Files</h3>
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>File Name</TableHead>
              <TableHead>Upload Date</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {upload_files.map((file) => (
              <TableRow key={file.id}>
                <TableCell className="font-medium">{file.name}</TableCell>
                <TableCell>
                  {new Date(file.uploadDate).toLocaleDateString()}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-2">
                    <button
                      onClick={() => handleViewFile(file.id, file.name)}
                      className="p-1 text-gray-600 hover:text-blue-600 transition-colors"
                      title="View CSV contents"
                    >
                      <Eye className="h-5 w-5" />
                    </button>
                    <button
                        onClick={() => handleProcessFile(file.id, file.name)}
                        className={`p-1 transition-colors ${
                            processingFiles[file.id]
                            ? 'text-yellow-600'
                            : 'text-gray-600 hover:text-green-600'
                        }`}
                        disabled={processingFiles[file.id]}
                        title="Process CSV file"
                        >
                        {processingFiles[file.id] ? (
                            <Loader2 className="h-5 w-5 animate-spin" />
                        ) : (
                            <Play className="h-5 w-5" />
                        )}
                    </button>
                    <button
                      onClick={() => handleDeleteFile(file.id, file.name)}
                      className="p-1 text-gray-600 hover:text-red-600 transition-colors"
                      title="Delete file"
                    >
                      <Trash2 className="h-5 w-5" />
                    </button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {upload_files.length === 0 && (
              <TableRow>
                <TableCell colSpan={3} className="text-center text-gray-500">
                  No files uploaded yet
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      {/* Process Message Display */}
      {processMessage && (
        <div className="mb-4 p-3 bg-green-100 text-green-700 rounded">
          {processMessage}
        </div>
      )}
        
      {/* CSV Viewer */}
      {viewedFile.content && (
        <CSVViewer 
          content={viewedFile.content} 
          fileName={viewedFile.name} 
        />
      )}
    </div>
  );
};

export default ProjectDetail;